/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import chunk from '@linc-technologies/linced-common/utils/chunk';
import mapWithConcurrency from '@linc-technologies/linced-common/utils/map-with-concurrency';
import { service } from '@ember/service';
import { scheduleOnce } from '@ember/runloop';
import { action } from '@ember/object';
import { tagName } from '@ember-decorators/component';

// Cap on simultaneous preload requests. The browser allows ~6 connections per
// origin; draining the preload fan-out through a pool of this size keeps the
// request queue from spiking at the start of a run.
const PRELOAD_CONCURRENCY = 6;

// Max person IDs per batched progression query. The IDs are UUIDs serialised
// into the GET query string, so an unbounded batch could exceed a proxy's
// ~8KB URL limit (414). 50 keeps the URL well under that while still collapsing
// the per-(subject × student) fan-out to ceil(students / 50) per subject.
const PROGRESSION_PERSON_CHUNK = 50;

// Drives the "publish" action for a set of student reports, capturing each
// report's strand/progress visuals immediately before it is published. For
// every selected report, in sequence: render it off-screen, run ReportView's
// captureVisuals() to (re)snapshot strandSliderVisual / progressVisual /
// strandTableVisual onto its sections, and only then POST the publish.
//
// Why capture at publish time: a report's saved visuals can be missing (it
// reached "ready to publish" before capture-on-mark-ready existed) or stale
// (its data changed after mark-ready), and neither is detectable by inspection.
// Re-capturing at the moment of publish guarantees every published report
// carries visuals that match its data as of publish.
//
// TEMPORARY: this whole flow is intended to be stripped out once we're
// confident every outstanding report with broken/missing visuals has been
// republished. The store-warming preload below is deliberately duplicated from
// bulk-pdf-download rather than shared, so removal is a clean delete and we
// don't touch the PDF renderer while it's being changed elsewhere.
//
// The run is externally triggered (the publish sidepeek's save button) via the
// @onRegisterRun hook, blocks tab-close while in flight, and renders a
// non-dismissable progress modal. A capture failure for a report means that
// report is NOT published (it stays ready-to-publish) and is counted as a
// failure; the loop continues with the rest.
@tagName('')
export default class PublishReportsRunner extends Component {
  @service
  store;

  @service
  toaster;

  // @reportTemplate — fallback template record for rendering
  // @studentReports — student report records to capture + publish, in order
  // @student — optional pre-resolved student (single-student context)
  // @publishReport — async fn(studentReport) that performs the publish POST
  // @onRegisterRun — called with run() so the parent can trigger the flow.
  //   run() resolves to { total, published, failures, failed }, or null if it
  //   aborted or errored before completing (the runner toasts its own error in
  //   that case). `failed` is the names of reports left ready-to-publish; the
  //   caller surfaces them in a single summary toast.

  @tracked
  isRunning = false;

  @tracked
  _currentStudent = null;
  @tracked
  _currentStudentReport = null;
  @tracked
  _currentTemplate = null;
  @tracked
  _progress = 0;
  @tracked
  _total = 0;
  @tracked
  _captureVisuals = null;
  @tracked
  _verifyCaptured = null;
  @tracked
  _waitForDataReady = null;
  _unloadHandler = null;
  _runRegistered = false;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Register the run trigger with the parent exactly once. The guard is
    // intentionally never reset, so subsequent didReceiveAttrs calls (e.g. when
    // @studentReports changes after a partial-failure refresh) won't re-register —
    // the parent re-registers itself via the action if it ever needs to.
    if (this.onRegisterRun && !this._runRegistered) {
      this._runRegistered = true;
      this.onRegisterRun(this.run.bind(this));
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this._removeUnloadGuard();
    if (this.onRegisterRun) {
      this.onRegisterRun(null);
    }
  }

  _installUnloadGuard() {
    if (this._unloadHandler) {
      return;
    }
    this._unloadHandler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', this._unloadHandler);
  }

  _removeUnloadGuard() {
    if (this._unloadHandler) {
      window.removeEventListener('beforeunload', this._unloadHandler);
      this._unloadHandler = null;
    }
  }

  // Warm the store so each report renders synchronously off-screen and its
  // charts resolve real data. Duplicated from bulk-pdf-download (see TEMPORARY
  // note above); returns the resolved students plus lookups for mapping a
  // student back to its report and template version.
  async _preloadRenderData(reports) {
    // Load sections for all reports before starting so renders are complete.
    await mapWithConcurrency(
      reports.map(
        (r) => () =>
          this.store
            .query('reports/student-report-section', {
              studentReportId: r.get('id'),
            })
            .catch(() => []),
      ),
      PRELOAD_CONCURRENCY,
    );

    // Resolve students from the report's studentId relationship, falling back
    // to @student when the API omits student_id on the report.
    const students = this.student
      ? [this.student]
      : (
          await mapWithConcurrency(
            reports.map(
              (r) => () =>
                this.store
                  .findRecord('person', r.belongsTo('studentId').id())
                  .catch(() => null),
            ),
            PRELOAD_CONCURRENCY,
          )
        ).filter(Boolean);

    // Each report may reference a different template version. Rendering with
    // the wrong version makes studentSectionMap lookups miss and strand visuals
    // render empty, so resolve each report's own template and load its sections.
    const uniqueTemplateIds = new Set();
    reports.forEach((r) => {
      const tid = r.belongsTo('reportTemplateId').id();
      if (tid) {
        uniqueTemplateIds.add(tid);
      }
    });

    await mapWithConcurrency(
      Array.from(uniqueTemplateIds).map((tid) => () => {
        const hasSections = this.store
          .peekAll('reports/report-template-section')
          .some((s) => s.belongsTo('reportTemplateId').id() === tid);
        if (hasSections) {
          return Promise.resolve();
        }
        // `reload: true` is required: report records sideload the template
        // without its sections, and a plain findRecord short-circuits on the
        // cached template, ignoring the includeSections adapterOption.
        return this.store
          .findRecord('reports/report-template', tid, {
            reload: true,
            adapterOptions: { includeSections: true },
          })
          .catch(() => null);
      }),
      PRELOAD_CONCURRENCY,
    );

    const templateByReportId = {};
    reports.forEach((r) => {
      const tid = r.belongsTo('reportTemplateId').id();
      templateByReportId[r.get('id')] =
        this.store.peekRecord('reports/report-template', tid) ||
        this.reportTemplate;
    });

    // Collect subject IDs and check for assessment-data sections across every
    // template version we're rendering.
    const subjectIds = new Set();
    let hasAssessmentData = false;
    uniqueTemplateIds.forEach((tid) => {
      this.store
        .peekAll('reports/report-template-section')
        .filter((s) => s.belongsTo('reportTemplateId').id() === tid)
        .forEach((section) => {
          const config = section.get('configuration');
          if (!config) {
            return;
          }
          try {
            const parsed =
              typeof config === 'string' ? JSON.parse(config) : config;
            if (parsed && parsed.subjectId) {
              subjectIds.add(parsed.subjectId);
            }
            if (
              parsed &&
              (parsed.components || []).some(
                (c) => c.type === 'assessment-data',
              )
            ) {
              hasAssessmentData = true;
            }
          } catch (e) {
            // malformed configuration — skip
          }
        });
    });

    // Mirror the view route's per-subject preload pattern. Progressions and
    // levels/expectations must be queried with goalSubjectId (and progressions
    // with `type`, not `progressionType`) to match what the strand/progress
    // components peek for — a global findAll returns an empty/different slice
    // and leaves charts blank.
    // Warm every distinct school in the batch — a group can span multiple
    // schools, and students[0]'s school_id may be null while later students'
    // are not. Sourcing only students[0] leaves ReportView's schoolRecord
    // falling back to session.school for students 2..N.
    const schoolIds = new Set(
      students.map((s) => s.get('school_id')).filter(Boolean),
    );
    const personIds = students.map((s) => s.get('personId') || s.get('id'));
    const personIdChunks = chunk(personIds, PROGRESSION_PERSON_CHUNK);
    // Thunks (() => Promise), drained through a bounded pool below rather than
    // started all at once — see PRELOAD_CONCURRENCY.
    const preloads = [
      // The reporting route fires this query but does not await it, so without
      // milestones in the store report-progress-visual filters out every record
      // and the chart line is empty on the first attempt. Awaiting here makes
      // the capture deterministic.
      () =>
        this.store
          .query('milestones/milestone-set', { include: 'milestones' })
          .catch(() => null),
    ];
    schoolIds.forEach((schoolId) => {
      preloads.push(() =>
        this.store.findRecord('school', schoolId).catch(() => null),
      );
    });
    if (hasAssessmentData) {
      preloads.push(
        () => this.store.findAll('assessments/assessment').catch(() => null),
        () => this.store.findAll('assessments/module').catch(() => null),
      );
      students.forEach((student) => {
        const personId = student.get('personId') || student.get('id');
        preloads.push(() =>
          this.store
            .query('assessments/grade', { personId, latest: true })
            .catch(() => null),
        );
      });
    }
    subjectIds.forEach((subjectId) => {
      preloads.push(() =>
        this.store.findRecord('goals/subject', subjectId).catch(() => null),
      );
      preloads.push(() =>
        this.store
          .query('milestones/level', { goalSubjectId: subjectId })
          .catch(() => null),
      );
      preloads.push(() =>
        this.store
          .query('milestones/expectation', { goalSubjectId: subjectId })
          .catch(() => null),
      );
      // One batched query per (subject × chunk of students) instead of one per
      // (subject × student): milestones/progression accepts a personIds list
      // and, with latest=true, groups per person server-side, so the result set
      // is identical to firing a query per student. Chunked to keep the GET URL
      // under the proxy limit (see PROGRESSION_PERSON_CHUNK).
      personIdChunks.forEach((idsChunk) => {
        preloads.push(() =>
          this.store
            .query('milestones/progression', {
              goalSubjectId: subjectId,
              personIds: JSON.stringify(idsChunk),
              type: ProgressionType.PROGRESSION,
              latest: true,
            })
            .catch(() => null),
        );
      });
    });
    await mapWithConcurrency(preloads, PRELOAD_CONCURRENCY);

    const reportByStudentId = {};
    reports.forEach((r) => {
      reportByStudentId[r.belongsTo('studentId').id()] = r;
    });

    const resolveReportForStudent = this.student
      ? () => reports[0]
      : (student) => reportByStudentId[student.get('id')];

    return { students, templateByReportId, resolveReportForStudent };
  }

  async run() {
    if (this.isRunning) {
      return;
    }
    const reports = this.studentReports;
    if (!reports || !reports.length) {
      return;
    }

    this.isRunning = true;
    this._progress = 0;
    this._total = 0;
    this._currentStudent = null;
    this._currentStudentReport = null;
    this._currentTemplate = null;
    this._installUnloadGuard();

    let failedNames = [];
    let published = 0;
    let students = [];
    try {
      let templateByReportId, resolveReportForStudent;
      ({ students, templateByReportId, resolveReportForStudent } =
        await this._preloadRenderData(reports));

      // The component should not be torn down mid-run (the parent blocks the
      // sidepeek from closing), but guard anyway — Ember classic throws on
      // set-after-destroy.
      if (this.isDestroyed || this.isDestroying) {
        return null;
      }
      if (!students.length) {
        // Every student failed to preload (e.g. network outage) — surface a
        // toast and return null so the caller treats it as an aborted run
        // rather than a silent success.
        this.toaster.error('Failed to publish reports. Please try again.');
        return null;
      }
      this._total = students.length;

      for (const student of students) {
        if (this.isDestroyed || this.isDestroying) {
          return null;
        }
        const currentReport = resolveReportForStudent(student);
        if (!currentReport) {
          continue;
        }
        // Count the student now in flight so the modal reads "X of N" for the
        // item being processed (1-based), not the count already published.
        this.incrementProperty('_progress');

        // Swap the off-screen ReportView onto the next student. The {{#if
        // _currentStudent}} stays truthy across iterations, so Ember REUSES the
        // same ReportView instance and just updates its attrs — didInsertElement
        // fires only once. We must NOT clear _captureVisuals / _waitForDataReady
        // here: they're registered on that first insert, bound to the persisting
        // instance, and stay valid as it re-renders + reloads for each student.
        // Clearing them would strand reports 2..N with no capture hook.
        this._currentStudent = student;
        this._currentStudentReport = currentReport;
        this._currentTemplate =
          templateByReportId[currentReport.get('id')] || this.reportTemplate;

        // Wait for the off-screen ReportView to render and register its hooks.
        await new Promise((resolve) =>
          scheduleOnce('afterRender', null, resolve),
        );
        if (this.isDestroyed || this.isDestroying) {
          return null;
        }
        // Wait for async data (assessment grades / attendance) to settle.
        if (this._waitForDataReady) {
          await this._waitForDataReady();
          if (this.isDestroyed || this.isDestroying) {
            return null;
          }
        }
        // Double rAF after data settles: ResizeObserver callbacks fire between
        // frames, so two animation frames ensure ChartResizer has its final
        // dimensions and the SVGs are painted before we serialise them.
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve)),
        );
        if (this.isDestroyed || this.isDestroying) {
          return null;
        }

        // ReportView never re-registered its capture hook (e.g. it failed to
        // mount) — do NOT publish a report whose visuals we couldn't snapshot.
        if (!this._captureVisuals) {
          failedNames.push(student.get('fullName'));
          continue;
        }
        try {
          await this._captureVisuals();
        } catch (e) {
          failedNames.push(student.get('fullName'));
          continue;
        }
        if (this.isDestroyed || this.isDestroying) {
          return null;
        }

        // Verify the charts actually serialised. verifyCapturedVisuals only
        // flags visuals whose element is present in the DOM but whose field is
        // still empty — i.e. the chart hadn't painted when captureVisuals ran
        // (a settle/timing race). An absent element means "nothing to draw" and
        // is not flagged. There is no backend backstop for missing visuals (see
        // docs/reporting/publish-snapshot.md), so this is the only guard against
        // freezing a blank chart into the published snapshot. Retry the settle +
        // capture once; if a visual is still missing, do NOT publish — leave the
        // report ready-to-publish for a retry rather than publish it blank.
        let missing = this._verifyCaptured ? this._verifyCaptured() : [];
        if (missing.length) {
          if (this._waitForDataReady) {
            await this._waitForDataReady();
            if (this.isDestroyed || this.isDestroying) {
              return null;
            }
          }
          await new Promise((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(resolve)),
          );
          if (this.isDestroyed || this.isDestroying) {
            return null;
          }
          try {
            await this._captureVisuals();
          } catch (e) {
            // A retry save failure is definitive: captureVisuals sets the
            // serialised value on the in-memory model before awaiting save, so
            // verifyCapturedVisuals would read that value and wrongly report no
            // missing visuals — publishing a blank visual the backend never
            // acknowledged. Count it as a failure and skip publishing.
            failedNames.push(student.get('fullName'));
            continue;
          }
          if (this.isDestroyed || this.isDestroying) {
            return null;
          }
          missing = this._verifyCaptured ? this._verifyCaptured() : [];
        }
        if (missing.length) {
          failedNames.push(student.get('fullName'));
          continue;
        }

        // Visuals captured and saved — publish this one report before moving on.
        try {
          await this.publishReport(currentReport);
          published++;
        } catch (e) {
          failedNames.push(student.get('fullName'));
        }
        if (this.isDestroyed || this.isDestroying) {
          return null;
        }
      }
    } catch (e) {
      this.toaster.error('Failed to publish reports. Please try again.');
      return null;
    } finally {
      this._removeUnloadGuard();
      if (!this.isDestroyed && !this.isDestroying) {
        this.isRunning = false;
        this._currentStudent = null;
        this._currentStudentReport = null;
        this._currentTemplate = null;
      }
    }

    if (this.isDestroyed || this.isDestroying) {
      return null;
    }
    // Return the summary rather than firing a callback mid-run: the caller
    // resets its own isPublishing flag in a finally and then handles the
    // close/refresh, which must happen after that flag clears.
    return {
      total: students.length,
      published,
      failures: failedNames.length,
      failed: failedNames,
    };
  }

  @action
  registerCapture(fn) {
    this._captureVisuals = fn;
  }

  @action
  registerVerifyCaptured(fn) {
    this._verifyCaptured = fn;
  }

  @action
  registerWaitForReady(fn) {
    this._waitForDataReady = fn;
  }
}
